// src/redux/streams/streamSlice.ts

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { streamApi } from "../../services/streams/streamApi";

import type {
  StreamMode,
  StreamResponse,
  StreamScope,
  StreamState,
  SupportedStreamKind,
} from "../../types/streams/streamTypes";

type ActiveStreamState = StreamState & {
  activeRequestId?: string | null;
};

const initialState: ActiveStreamState = {
  seedId: null,
  kind: null,

  subtype: null,
  scope: "square",
  mode: "related",

  items: [],
  next: null,

  extension: 0,
  canContinue: true,
  limitReached: false,

  loading: false,
  error: undefined,

  activeRequestId: null,
};

// ------------------------------------------------------------
// Open universal stream
// ------------------------------------------------------------
export const openStream = createAsyncThunk<
  {
    kind: SupportedStreamKind;
    seedId: number;
    scope: StreamScope;
    mode: StreamMode;
    username?: string | null;
    data: StreamResponse;
  },
  {
    kind: SupportedStreamKind;
    seedId: number;
    scope?: StreamScope;
    mode?: StreamMode;
    username?: string | null;
  },
  {
    rejectValue: string;
  }
>("streams/open", async (params, { rejectWithValue }) => {
  try {
    const scope = params.scope ?? "square";
    const mode = params.mode ?? "related";

    const data = await streamApi.fetch({
      kind: params.kind,
      seedId: params.seedId,
      scope,
      mode,
      extension: 0,
      username: params.username,
    });

    return {
      kind: params.kind,
      seedId: params.seedId,
      scope,
      mode,
      username: params.username ?? null,
      data,
    };
  } catch (err: any) {
    return rejectWithValue(
      err?.response?.data?.detail ||
        err?.message ||
        "Stream open failed"
    );
  }
});

// ------------------------------------------------------------
// Load next stream page
// ------------------------------------------------------------
export const loadMoreStream = createAsyncThunk<
  StreamResponse,
  void,
  {
    state: any;
    rejectValue: string;
  }
>("streams/loadMore", async (_, { getState, rejectWithValue }) => {
  try {
    const state = getState().stream as ActiveStreamState;

    if (!state.kind || !state.seedId) {
      throw new Error("Stream is not open.");
    }

    if (!state.canContinue || state.limitReached) {
      throw new Error("Stream limit reached.");
    }

    if (!state.next) {
      throw new Error("No next stream cursor.");
    }

    return await streamApi.fetch({
      kind: state.kind,
      seedId: state.seedId,
      scope: state.scope,
      mode: state.mode,
      cursor: state.next,
      extension: state.extension,
    });
  } catch (err: any) {
    return rejectWithValue(
      err?.response?.data?.detail ||
        err?.message ||
        "Stream continuation failed"
    );
  }
});

// ------------------------------------------------------------
// Load next extension
// Use this when backend returned no next cursor but can_continue=true.
// ------------------------------------------------------------
export const loadNextStreamExtension = createAsyncThunk<
  StreamResponse,
  void,
  {
    state: any;
    rejectValue: string;
  }
>("streams/loadNextExtension", async (_, { getState, rejectWithValue }) => {
  try {
    const state = getState().stream as ActiveStreamState;

    if (!state.kind || !state.seedId) {
      throw new Error("Stream is not open.");
    }

    if (!state.canContinue || state.limitReached) {
      throw new Error("Stream limit reached.");
    }

    return await streamApi.fetch({
      kind: state.kind,
      seedId: state.seedId,
      scope: state.scope,
      mode: state.mode,
      cursor: undefined,
      extension: state.extension + 1,
    });
  } catch (err: any) {
    return rejectWithValue(
      err?.response?.data?.detail ||
        err?.message ||
        "Stream extension failed"
    );
  }
});

// ------------------------------------------------------------
// Helpers
// ------------------------------------------------------------
const dedupeItems = (items: ActiveStreamState["items"]) => {
  const seen = new Set<string>();
  const output: ActiveStreamState["items"] = [];

  for (const item of items) {
    const key = `${item.kind}-${item.id}`;
    if (seen.has(key)) continue;

    seen.add(key);
    output.push(item);
  }

  return output;
};

const appendUniqueItems = (
  current: ActiveStreamState["items"],
  incoming: ActiveStreamState["items"]
) => {
  return dedupeItems([...current, ...incoming]);
};

// ------------------------------------------------------------
// Slice
// ------------------------------------------------------------
const streamSlice = createSlice({
  name: "stream",
  initialState,
  reducers: {
    closeStream: () => initialState,

    clearStreamError(state) {
      state.error = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // --------------------------------------------------------
      // Open
      // --------------------------------------------------------
      .addCase(openStream.pending, (state, action) => {
        const { kind, seedId, scope = "square", mode = "related" } =
          action.meta.arg;

        state.loading = true;
        state.error = undefined;
        state.activeRequestId = action.meta.requestId;

        // Arm new stream immediately to avoid stale UI.
        state.kind = kind;
        state.seedId = seedId;
        state.scope = scope;
        state.mode = mode;

        state.subtype = null;
        state.items = [];
        state.next = null;

        state.extension = 0;
        state.canContinue = true;
        state.limitReached = false;
      })
      .addCase(openStream.fulfilled, (state, action) => {
        if (
          state.activeRequestId &&
          action.meta.requestId !== state.activeRequestId
        ) {
          return;
        }

        const { kind, seedId, scope, mode, data } = action.payload;

        if (state.kind !== kind || state.seedId !== seedId) {
          return;
        }

        state.subtype = data.subtype;
        state.scope = data.scope || scope;
        state.mode = data.mode || mode;

        state.items = dedupeItems(data.results || []);
        state.next = data.next ?? null;

        state.extension = data.extension ?? 0;
        state.canContinue = Boolean(data.can_continue);
        state.limitReached = Boolean(data.limit_reached);

        state.loading = false;
        state.error = undefined;
        state.activeRequestId = null;
      })
      .addCase(openStream.rejected, (state, action) => {
        if (
          state.activeRequestId &&
          action.meta.requestId !== state.activeRequestId
        ) {
          return;
        }

        state.loading = false;
        state.activeRequestId = null;
        state.error = action.payload || "Could not open stream.";
      })

      // --------------------------------------------------------
      // Load more
      // --------------------------------------------------------
      .addCase(loadMoreStream.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(loadMoreStream.fulfilled, (state, action) => {
        const data = action.payload;

        state.items = appendUniqueItems(state.items, data.results || []);
        state.next = data.next ?? null;

        state.subtype = data.subtype ?? state.subtype;
        state.scope = data.scope ?? state.scope;
        state.mode = data.mode ?? state.mode;

        state.extension = data.extension ?? state.extension;
        state.canContinue = Boolean(data.can_continue);
        state.limitReached = Boolean(data.limit_reached);

        state.loading = false;
      })
      .addCase(loadMoreStream.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Could not load more stream items.";
      })

      // --------------------------------------------------------
      // Load next extension
      // --------------------------------------------------------
      .addCase(loadNextStreamExtension.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(loadNextStreamExtension.fulfilled, (state, action) => {
        const data = action.payload;

        state.items = appendUniqueItems(state.items, data.results || []);
        state.next = data.next ?? null;

        state.subtype = data.subtype ?? state.subtype;
        state.scope = data.scope ?? state.scope;
        state.mode = data.mode ?? state.mode;

        state.extension = data.extension ?? state.extension + 1;
        state.canContinue = Boolean(data.can_continue);
        state.limitReached = Boolean(data.limit_reached);

        state.loading = false;
      })
      .addCase(loadNextStreamExtension.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Could not continue stream.";
      });
  },
});

export const { closeStream, clearStreamError } = streamSlice.actions;

export default streamSlice.reducer;